import { supabase } from '@/lib/supabaseClient';

export type AuthAuditAction =
  | 'auth.login'
  | 'auth.login_failed'
  | 'auth.logout'
  | 'auth.rate_limited'
  | 'auth.register'
  | 'auth.register_failed'
  | 'auth.password_reset_requested'
  | 'auth.password_reset_failed'
  | 'auth.password_updated'
  | 'auth.password_update_failed'
  | 'auth.invite_activated'
  | 'auth.invite_activation_failed'
  | 'auth.account_locked'
  | 'auth.account_closure_requested'
  | 'auth.account_closure_failed'
  | 'auth.account_reactivated'
  | 'auth.account_closure_effective'
  | 'gdpr.rights_request_created'
  | 'gdpr.rights_request_resolved';

export type AuditSeverity = 'info' | 'warning' | 'critical';

export interface AuthAuditDetails {
  reason?: string;
  errorMessage?: string;
  errorCode?: string;
  consecutiveFailures?: number;
  retryAfterSeconds?: number;
  clientTimestamp?: string;
  [key: string]: unknown;
}

export interface LogAuthEventParams {
  action: AuthAuditAction;
  severity: AuditSeverity;
  email?: string | null;
  userId?: string | null;
  communityId?: string | null;
  resourceType?: string;
  resourceId?: string | null;
  details?: AuthAuditDetails;
}

export interface RateLimitStatus {
  isRateLimited: boolean;
  consecutiveFailures: number;
  retryAfterSeconds: number;
}

const STORAGE_KEY_PREFIX = 'comunihome_auth_failures_';
const MAX_CONSECUTIVE_FAILURES = 5;
const LOCKOUT_WINDOW_MS = 5 * 60 * 1000; // 5 minutos

interface FailureRecord {
  count: number;
  firstFailureAt: number;
  lastFailureAt: number;
}

const getStorageKey = (email: string): string => {
  const normalized = email.trim().toLowerCase();
  return `${STORAGE_KEY_PREFIX}${normalized || 'anonymous'}`;
};

const readFailureRecord = (email: string): FailureRecord | null => {
  try {
    const raw = sessionStorage.getItem(getStorageKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FailureRecord>;
    if (
      typeof parsed.count === 'number' &&
      typeof parsed.firstFailureAt === 'number' &&
      typeof parsed.lastFailureAt === 'number'
    ) {
      return {
        count: parsed.count,
        firstFailureAt: parsed.firstFailureAt,
        lastFailureAt: parsed.lastFailureAt,
      };
    }
    return null;
  } catch {
    return null;
  }
};

const writeFailureRecord = (email: string, record: FailureRecord | null): void => {
  try {
    const key = getStorageKey(email);
    if (!record) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(record));
    }
  } catch {
    // Storage no disponible o cuota superada
  }
};

/**
 * Consulta si un usuario/email está actualmente bloqueado por exceso de intentos fallidos.
 */
export function checkLoginRateLimit(email: string): RateLimitStatus {
  const record = readFailureRecord(email);
  if (!record) {
    return { isRateLimited: false, consecutiveFailures: 0, retryAfterSeconds: 0 };
  }

  const now = Date.now();
  const timeSinceFirst = now - record.firstFailureAt;

  if (timeSinceFirst > LOCKOUT_WINDOW_MS) {
    writeFailureRecord(email, null);
    return { isRateLimited: false, consecutiveFailures: 0, retryAfterSeconds: 0 };
  }

  if (record.count >= MAX_CONSECUTIVE_FAILURES) {
    const remainingMs = Math.max(0, LOCKOUT_WINDOW_MS - timeSinceFirst);
    const retryAfterSeconds = Math.max(1, Math.ceil(remainingMs / 1000));
    return {
      isRateLimited: true,
      consecutiveFailures: record.count,
      retryAfterSeconds,
    };
  }

  return {
    isRateLimited: false,
    consecutiveFailures: record.count,
    retryAfterSeconds: 0,
  };
}

/**
 * Registra un intento fallido y evalúa si se supera el umbral de rate limiting.
 */
export function recordFailedLoginAttempt(email: string): RateLimitStatus {
  const now = Date.now();
  const current = readFailureRecord(email);

  let updated: FailureRecord;

  if (!current || now - current.firstFailureAt > LOCKOUT_WINDOW_MS) {
    updated = {
      count: 1,
      firstFailureAt: now,
      lastFailureAt: now,
    };
  } else {
    updated = {
      count: current.count + 1,
      firstFailureAt: current.firstFailureAt,
      lastFailureAt: now,
    };
  }

  writeFailureRecord(email, updated);

  const isRateLimited = updated.count >= MAX_CONSECUTIVE_FAILURES;
  const remainingMs = Math.max(0, LOCKOUT_WINDOW_MS - (now - updated.firstFailureAt));
  const retryAfterSeconds = isRateLimited ? Math.max(1, Math.ceil(remainingMs / 1000)) : 0;

  return {
    isRateLimited,
    consecutiveFailures: updated.count,
    retryAfterSeconds,
  };
}

/**
 * Restablece el contador de intentos fallidos tras un inicio de sesión satisfactorio.
 */
export function resetFailedLoginAttempts(email: string): void {
  writeFailureRecord(email, null);
}

/**
 * Envía un evento de auditoría de autenticación a la base de datos de manera segura y no bloqueante.
 */
export async function logAuthEvent(params: LogAuthEventParams): Promise<string | null> {
  const normalizedEmail = params.email ? params.email.trim().toLowerCase() : null;

  try {
    const rpcPayload = {
      p_action: params.action,
      p_severity: params.severity,
      p_email: normalizedEmail,
      p_details: {
        ...params.details,
        clientTimestamp: new Date().toISOString(),
      },
      p_resource_type: params.resourceType ?? 'auth',
      p_resource_id: params.resourceId ?? null,
      p_user_id: params.userId ?? null,
      p_community_id: params.communityId ?? null,
    };

    const response = await supabase.rpc('log_auth_audit_event', rpcPayload);

    if (response.error) {
      console.warn('[authAuditService] Error al registrar evento de auditoría:', response.error.message);
      return null;
    }

    return typeof response.data === 'string' ? response.data : null;
  } catch (error: unknown) {
    console.warn('[authAuditService] Excepción al registrar evento de auditoría:', error);
    return null;
  }
}
